import { datadogRum } from '@datadog/browser-rum';
import versionJSON from '../../public/version.json';

const isClient = typeof window !== 'undefined';
const applicationId = process.env.NEXT_PUBLIC_DATA_DOG_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DATA_DOG_CLIENT_TOKEN;
const graphqlURI = process.env.GRAPHQL_URI;
const env = versionJSON.environment;
const version = versionJSON.version;

const allowedEnvironments = new Set([
  'production',
  'staging',
  'trial',
  'preview',
  'dev',
]);

interface PerformanceMemory {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

const getPerfMemory = () => {
  const memory = (performance as PerformanceWithMemory).memory;
  const hasAllProps =
    memory &&
    ['jsHeapSizeLimit', 'totalJSHeapSize', 'usedJSHeapSize'].every(
      (prop) => prop in memory,
    );
  if (hasAllProps) {
    return memory;
  }
  return null;
};

if (isClient && allowedEnvironments.has(env) && applicationId && clientToken) {
  console.info('Datadog RUM enabled', { versionJSON });
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.eu',
    service: 'frontend',
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    beforeSend(event) {
      const pathname = window.location.pathname;
      const isSignInOrRootPath = new Set(['signin', '/']).has(pathname);
      const isNetworkError =
        event.type === 'error' && event.error.message.includes('networkError');
      // Do not send network errors from sign-in page
      if (isSignInOrRootPath && isNetworkError) {
        return false;
      }

      const memory = getPerfMemory();
      if (memory) {
        event.context = {
          ...event.context,
          memory: {
            used_mb: Math.round(memory.usedJSHeapSize / (1024 * 1024)),
            total_mb: Math.round(memory.totalJSHeapSize / (1024 * 1024)),
            total_percent: Math.round(
              (memory.totalJSHeapSize / memory.jsHeapSizeLimit) * 100,
            ),
          },
        };
      }

      return true;
    },
    allowedTracingUrls: [
      {
        match: `${graphqlURI}/graphql`,
        propagatorTypes: ['tracecontext'],
      },
    ],
  });
}

export default function DatadogInit() {
  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null;
}
